import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnInit {
  resetForm: FormGroup;
  isSubmitting = false;
  isSuccess = false;
  errorMessage: string | null = null;
  showPassword = false;
  showConfirmPassword = false;
  oobCode: string | null = null;
  isValidCode = false;
  isVerifyingCode = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordsMatchValidator.bind(this) });
  }

  async ngOnInit() {
    this.oobCode = this.route.snapshot.queryParamMap.get('oobCode');
    
    if (!this.oobCode) {
      this.errorMessage = "Invalid or missing password reset link.";
      this.isVerifyingCode = false;
      return;
    }

    try {
      await this.authService.verifyResetCode(this.oobCode);
      this.isValidCode = true;
    } catch (error: any) {
      console.error('Invalid reset code', error);
      this.errorMessage = "The password reset link is invalid or has expired.";
    } finally {
      this.isVerifyingCode = false;
    }
  }

  get password() {
    return this.resetForm.get('password');
  }

  get confirmPassword() {
    return this.resetForm.get('confirmPassword');
  }

  private passwordsMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (!password || !confirm) return null;
    return password === confirm ? null : { passwordsMismatch: true };
  }

  async submit() {
    if (this.resetForm.invalid || this.isSubmitting || !this.oobCode) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = null;

    try {
      const { password } = this.resetForm.value;
      await this.authService.confirmReset(this.oobCode, password);
      this.isSuccess = true;
      // Optional: Redirect after delay
      setTimeout(() => this.router.navigate(['/sign-in']), 3000);
    } catch (error: any) {
      console.error('Password reset failed', error);
      this.errorMessage = error.message || 'Failed to reset password. Please try again.';
      this.isSubmitting = false;
    }
  }
}
